import { Editor, Range } from 'domains/reporter/RichTextEditor/core';

import { END_OF_SENTENCE_PUNCTUATION } from '../stitching/normalizationHelpers';

import { getParagraphRangeAtPoint, getSelectionForDictation } from './selection';

/**
 * Distilled, paragraph-scoped context around the caret or selection, sent to ASR Plex so the
 * backend can reason about the sentence being edited without receiving the full document.
 */
export type CursorContext = {
  sentenceBefore: string;
  sentenceAfter: string;
  selectedText: string;
};

const END_OF_SENTENCE_CHARS = new Set(END_OF_SENTENCE_PUNCTUATION.split(''));

// The current sentence begins after the last end-of-sentence boundary. The colon is intentionally
// excluded here (unlike findLastIndexOfPunctuation, which treats ":" as punctuation) because in
// this editor a colon is a heading separator, not a sentence terminator.
const sliceAfterLastSentenceBoundary = (text: string): string => {
  let boundaryIndex = -1;
  for (let i = 0; i < text.length; i++) {
    if (END_OF_SENTENCE_CHARS.has(text[i])) {
      boundaryIndex = i;
    }
  }
  return text.slice(boundaryIndex + 1);
};

// The current sentence ends at the first end-of-sentence boundary. The terminating punctuation is
// excluded from the returned text.
const sliceBeforeFirstSentenceBoundary = (text: string): string => {
  for (let i = 0; i < text.length; i++) {
    if (END_OF_SENTENCE_CHARS.has(text[i])) {
      return text.slice(0, i);
    }
  }
  return text;
};

/**
 * Computes the cursor context for the given selection.
 *
 * The before and after text never cross a block boundary: the before side is scoped to the block
 * containing the selection start and the after side to the block containing the selection end. For
 * an expanded selection that spans multiple paragraphs this keeps each side within its own block.
 */
export const getCursorContext = (editor: Editor, selection: Range): CursorContext => {
  // Mirror dictation's word-end snap so the context matches the real insertion point. The snap is
  // computed against the passed `selection`, not the live `editor.selection`, so a snapshotted caret
  // (e.g. a recorded ASR Plex modify selection) is snapped relative to itself even if the live caret
  // has moved. Only collapsed carets snap; getSelectionForDictation returns null when there's nothing
  // to snap, and expanded selections are used as-is.
  const effectiveSelection = Range.isCollapsed(selection)
    ? (getSelectionForDictation(editor, selection) ?? selection)
    : selection;

  const [startPoint, endPoint] = Range.edges(effectiveSelection);

  const beforeBlockRange = getParagraphRangeAtPoint(editor, startPoint);
  const afterBlockRange = getParagraphRangeAtPoint(editor, endPoint);

  let sentenceBefore = '';
  if (beforeBlockRange != null) {
    const beforeRaw = Editor.string(editor, {
      anchor: beforeBlockRange.anchor,
      focus: startPoint,
    });
    sentenceBefore = sliceAfterLastSentenceBoundary(beforeRaw).trim();
  }

  let sentenceAfter = '';
  if (afterBlockRange != null) {
    const afterRaw = Editor.string(editor, {
      anchor: endPoint,
      focus: afterBlockRange.focus,
    });
    sentenceAfter = sliceBeforeFirstSentenceBoundary(afterRaw).trim();
  }

  const selectedText = Range.isCollapsed(selection) ? '' : Editor.string(editor, selection);

  return { sentenceBefore, sentenceAfter, selectedText };
};
